import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { organizationService } from '../services/organization';
import { whatsappService, WhatsAppMessageLog } from '../services/whatsapp';
import { MessageCircle, ChevronLeft, ChevronRight, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const triggerLabel = (t: WhatsAppMessageLog['trigger']) =>
  t === 'SETTLE_AUTO' ? 'Auto (Settle)' : 'Manual Share';

/** Platform-owner WhatsApp billing logs: pick an organization + month/year. */
export const SuperAdminWhatsAppLogs: React.FC = () => {
  const now = new Date();
  const [orgCode, setOrgCode] = useState('');
  const [month, setMonth] = useState<number | ''>(now.getMonth() + 1);
  const [year, setYear] = useState<number | ''>(now.getFullYear());
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: organizations = [] } = useQuery(['organizations-walogs'], organizationService.getOrganizations);

  // Auto-select first org.
  useEffect(() => {
    if (organizations.length > 0 && !orgCode) setOrgCode(organizations[0].code);
  }, [organizations, orgCode]);

  // Reset to page 0 whenever the filter changes.
  useEffect(() => { setPage(0); }, [orgCode, month, year]);

  const filterReady = !!orgCode;
  const { data, isLoading, isFetching, refetch } = useQuery(
    ['wa-logs-super', orgCode, month, year, page],
    () => whatsappService.getLogs(page, pageSize, {
      org: orgCode,
      month: month && year ? Number(month) : undefined,
      year: month && year ? Number(year) : undefined,
    }),
    { enabled: filterReady, keepPreviousData: true }
  );

  const logs = data?.content ?? [];
  const sentOnPage = logs.filter(l => l.status === 'SENT').length;
  const failedOnPage = logs.filter(l => l.status === 'FAILED').length;
  const years: number[] = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  const selectCls = 'px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#25D36622' }}>
          <MessageCircle className="h-5 w-5" style={{ color: '#25D366' }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">WhatsApp Logs</h1>
          <p className="text-sm text-gray-500">Per-organization billing record</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Organization</label>
          <select className={selectCls} value={orgCode} onChange={e => setOrgCode(e.target.value)}>
            {organizations.map(o => <option key={o.id} value={o.code}>{o.name} ({o.code})</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Year</label>
          <select className={selectCls} value={year} onChange={e => setYear(e.target.value ? Number(e.target.value) : '')}>
            <option value="">All</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Month</label>
          <select className={selectCls} value={month} onChange={e => setMonth(e.target.value ? Number(e.target.value) : '')}>
            <option value="">All</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        {!(month && year) && (
          <span className="text-xs text-gray-400 ml-auto self-center">Select both month &amp; year to filter to one billing month.</span>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <p className="text-sm text-gray-500">Total Messages</p>
          <p className="text-2xl font-bold mt-1 text-gray-900">{data?.totalElements ?? 0}</p>
        </div>
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <p className="text-sm text-gray-500">Sent (this page)</p>
          <p className="text-2xl font-bold mt-1 text-green-600">{sentOnPage}</p>
        </div>
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <p className="text-sm text-gray-500">Failed (this page)</p>
          <p className="text-2xl font-bold mt-1 text-red-600">{failedOnPage}</p>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No WhatsApp messages for this selection.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Date & Time', 'Order#', 'Mobile', 'Template', 'Trigger', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => (
                  <tr key={log.id}>
                    <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-800">{format(new Date(log.createdAt), 'dd MMM yyyy, hh:mm a')}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{log.orderNo || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{log.mobile}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{log.templateName}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.trigger === 'SETTLE_AUTO' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'}`}>
                        {triggerLabel(log.trigger)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {log.status === 'SENT' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3" /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" title={log.error || undefined}>
                          <XCircle className="h-3 w-3" /> Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(data?.totalPages ?? 0) > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">Page {(data?.number ?? 0) + 1} of {data?.totalPages} · {data?.totalElements} messages</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || isFetching}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= (data?.totalPages ?? 1) - 1 || isFetching}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
